import { Dialog, Transition } from "@headlessui/react";
import { XIcon } from "@heroicons/react/solid";
import { reduce, sumBy } from "lodash";
import React, { Fragment, useCallback, useMemo, useRef } from "react";
import { useCommissionReports } from ".";
import ContactComponent from "../../components/ContactComponent";
import QrCode from "../../components/QrCode";
import classNames from "../../utils/classNames";
import { CommissionReport } from "../../utils/types";
import { BONES_PER_HNT, useHntMarket } from "../../utils/useHntMarket";
import { ValidContactCommissions } from "./CommissionPaymentSummarySidebar";

interface Props {}

const HntPaymentSidebar: React.FC<Props> = () => {
  const dialogRef = useRef(null);
  const { displayInCurrency } = useHntMarket();
  const {
    contactCommissionsSummarySidebarConfig,
    hntPaymentSidebarConfig,
    setHntPaymentSidebarConfig,
    setContactCommissionsSummarySidebarConfig,
    commissionPaymentSummarySidebarConfig
  } = useCommissionReports();

  const isParentSidebar = useMemo(
    () =>
      contactCommissionsSummarySidebarConfig.parentSidebar === "hnt-payment",
    [contactCommissionsSummarySidebarConfig.parentSidebar]
  );

  const closeSidebar = useCallback(() => {
    if (isParentSidebar) return;

    setHntPaymentSidebarConfig({ show: false });
  }, [isParentSidebar, setHntPaymentSidebarConfig]);

  const {
    show,
    commissionReport,
    contactCommissionsToPay = []
  } = useMemo(() => hntPaymentSidebarConfig, [hntPaymentSidebarConfig]);

  const renderContent = useMemo(() => {
    if (!commissionReport) return null;

    const memo = commissionReport.uuid.substr(-PAYMENT_MEMO_MAX_CHARACTERS);

    return (
      <div className="flex-1 py-6 divide-y divide-gray-200 overflow-y-auto">
        <div className="px-4 sm:px-6 space-y-6">
          <div className="w-full">
            <QrCode
              className="w-full"
              size={398}
              background="#fff"
              fill="#A96CEB"
              text={getHntBulkPaymentJson(contactCommissionsToPay, memo)}
              radius={0.5}
            />
          </div>

          <div className="w-full flex flex-col">
            <h3 className="text-sm font-medium text-gray-500 flex items-center justify-between">
              <span>
                Contact
                {contactCommissionsToPay.length === 1 ? "" : "s"} (
                {contactCommissionsToPay.length})
              </span>
              <span>Amounts</span>
            </h3>
            <div className="w-full mt-2 border-t border-gray-200 divide-y divide-gray-200">
              {contactCommissionsToPay.map((contactCommissions) => (
                <div
                  key={contactCommissions.contact.uuid}
                  className="flex items-start justify-between text-sm font-medium group cursor-pointer"
                  onClick={() => {
                    setContactCommissionsSummarySidebarConfig({
                      show: true,
                      commissionReport,
                      contactCommissions,
                      parentSidebar: "hnt-payment",
                      getPaymentStatus:
                        commissionPaymentSummarySidebarConfig.getPaymentStatus
                    });
                  }}
                >
                  <div className="relative flex items-center space-x-3">
                    <ContactComponent
                      contactId={contactCommissions.contact.uuid}
                      fallbackName={contactCommissions.contact.name}
                      withoutDarkMode
                    />
                  </div>
                  <div className="text-md font-semibold text-gray-900">
                    {displayInCurrency(
                      contactCommissions.totalCommissionValue,
                      {
                        withHntSuffix: true,
                        needsDivision: true
                      }
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }, [
    commissionPaymentSummarySidebarConfig.getPaymentStatus,
    commissionReport,
    contactCommissionsToPay,
    displayInCurrency,
    setContactCommissionsSummarySidebarConfig
  ]);

  return (
    <Transition.Root show={show} as={Fragment}>
      <Dialog
        as="div"
        static
        className="fixed inset-0 z-20 overflow-hidden"
        open={show}
        onClose={closeSidebar}
        initialFocus={dialogRef}
      >
        <div ref={dialogRef} className="absolute inset-0 overflow-hidden">
          <Dialog.Overlay className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

          <div
            className={classNames(
              "fixed inset-y-0 pl-16 max-w-full right-0 flex duration-500",
              isParentSidebar && "pr-40"
            )}
          >
            <div className="absolute inset-0" />
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <div className="relative w-screen max-w-md">
                <div className="max-w-3xl xl:max-w-6xl h-full">
                  <div className="h-full flex flex-col bg-white shadow-xl">
                    <div className="flex-1 h-0 flex flex-col">
                      <div className="flex-none py-6 px-4 bg-hotspotty-700 sm:px-6">
                        <div className="flex items-center justify-between">
                          <Dialog.Title className="text-lg font-medium text-white">
                            Pay{" "}
                            {displayInCurrency(
                              sumBy(
                                contactCommissionsToPay,
                                "totalCommissionValue"
                              ),
                              {
                                withHntSuffix: true,
                                needsDivision: true
                              }
                            )}{" "}
                            to {contactCommissionsToPay.length} contact
                            {contactCommissionsToPay.length === 1 ? "" : "s"}
                          </Dialog.Title>
                          <div className="ml-3 h-7 flex items-center">
                            <button
                              type="button"
                              className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400"
                              onClick={closeSidebar}
                            >
                              <span className="sr-only">Close panel</span>
                              <XIcon className="h-6 w-6" aria-hidden="true" />
                            </button>
                          </div>
                        </div>
                      </div>
                      {renderContent}
                    </div>
                    <div className="flex-shrink-0 px-4 py-4 flex justify-end">
                      <button
                        type="button"
                        className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400"
                        onClick={closeSidebar}
                      >
                        Cancel
                      </button>
                      <button
                        className="ml-4 justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 flex items-center disabled:opacity-50 disabled:cursor-default disabled:hover:bg-hotspotty-400"
                        onClick={closeSidebar}
                      >
                        Done
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default HntPaymentSidebar;

//
// Utils
//

// TODO: extend this for other currencies
export interface HntPaymentSidebarConfig {
  show: boolean;
  commissionReport?: CommissionReport;
  contactCommissionsToPay?: ValidContactCommissions[];
  parentSidebar?: "commission-payment-summary";
}

export const PAYMENT_MEMO_MAX_CHARACTERS = 8;

export const getHntBulkPaymentJson = (
  validContactCommissions: ValidContactCommissions[],
  memo?: string | undefined
) =>
  memo
    ? JSON.stringify({
        type: "payment",
        utf8Memo: true,
        payees: reduce(
          validContactCommissions,
          (
            acc: {
              [walletAddress: string]: {
                amount: string;
                memo: string;
              };
            },
            { totalCommissionValue, walletAddress }
          ) => {
            const amount = (totalCommissionValue / BONES_PER_HNT).toString();
            acc[walletAddress] = { amount, memo };
            return acc;
          },
          {}
        )
      })
    : JSON.stringify({
        type: "payment",
        payees: reduce(
          validContactCommissions,
          (
            acc: {
              [walletAddress: string]: {
                amount: string;
              };
            },
            { totalCommissionValue, walletAddress }
          ) => {
            const amount = (totalCommissionValue / BONES_PER_HNT).toString();
            acc[walletAddress] = { amount };
            return acc;
          },
          {}
        )
      });
